import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button, IconButton } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Agreement, AgreementStatus, formatAgreementStatus } from '@/domain/agreements/agreement'
import { AssuredPerson, formatAssuredPersonStatus, getAssuredPersonStatusColor } from '@/domain/assured/Assured'
import { ServiceType } from '@/domain/offerings/offering'
import { PARCEL_SECTIONS, Parcel, ParcelState, formatParcelState } from '@/domain/parcels/parcel'
import { RedemptionType, formatRedemptionType } from '@/domain/redeem/redeem'
import { useAgreementAction } from '@/hooks/api/useAgreements'
import { useOffering } from '@/hooks/api/useOfferings'
import { useParcels } from '@/hooks/api/useParcels'
import { AgreementAction, AgreementActionRequest } from '@/rest/modules/agreements-api'
import { cn } from '@/utils/tailwind-util'
import { AlertCircle, AlertTriangle, Check, MapPin, Power, UserCheck, X } from 'lucide-react'
import { useEffect, useState } from 'react'

interface ActionDefinition {
  id: AgreementAction
  label: string
  icon: React.ReactNode
  alertTitle: string
  alertDescription: string
  onConfirm: () => void
  isPending: boolean
  disabled?: boolean
}

const needsNoActions = (serviceType: ServiceType | undefined) => {
  if (!serviceType) return true
  return false
}

const actionConsequences: Record<string, { title: string; description: string }> = {
  modifyStatus: {
    title: 'Cambiar estado del contrato',
    description: 'El estado del contrato será actualizado al valor seleccionado. Esto puede afectar la facturación y disponibilidad del servicio.',
  },
  suspend: {
    title: 'Suspender abono',
    description: 'El abono quedará suspendido y no generará períodos de facturación mientras esté en este estado. Podrá reactivarse posteriormente.',
  },
  redeem: {
    title: 'Redimir abonado',
    description: 'Se generá una solicitud de redención para el abonado seleccionado.',
  },
  terminate: {
    title: 'Finalizar contrato',
    description: 'El contrato de parcela será finalizado y la parcela quedará disponible. Podría generar ajustes en la facturación.',
  },
  changeParcel: {
    title: 'Cambiar parcela',
    description: 'La ubicación de la parcela será actualizada a la nueva ubicación seleccionada. Los datos del contrato se mantendrán.',
  },
}

const mapOptions = (code: string): RedemptionType[] => {
  switch (code) {
    case '56':
    case '57':
    case '58':
      return [RedemptionType.FUNERAL]
    case '54':
    case '55':
    case '66':
      return [RedemptionType.CREMATION]
    case '50':
    case '51':
    case '52':
    case '53':
    case '64':
    case '65':
    case '67':
      return [RedemptionType.PARCEL]
    case '59':
    case '60':
    case '61':
      return [RedemptionType.FUNERAL_PARCEL]
    case '62':
    case '63':
      return [RedemptionType.FUNERAL_CREMATION]
    default:
      return [RedemptionType.CREMATION]
  }
}

interface ContractActionsModalProps {
  agreement: Agreement
  isOpen: boolean
  onClose: () => void
  serviceType: ServiceType | undefined
  subscriptionId?: string
  parcelId?: string
  parcel?: Parcel
  people?: AssuredPerson[]
}

export const ContractActionsModal = ({
  agreement,
  isOpen,
  onClose,
  serviceType,
  subscriptionId,
  parcelId,
  parcel,
  people,
}: ContractActionsModalProps) => {
  const { data: offering } = useOffering(agreement?.offeringId)
  const [selectedAction, setSelectedAction] = useState<AgreementAction | null>(null)
  const [selectedPeopleIds, setSelectedPeopleIds] = useState<string[]>([])
  const [selectedRedemptionType, setSelectedRedemptionType] = useState<RedemptionType>(RedemptionType.CREMATION)
  const [selectedParcelSection, setSelectedParcelSection] = useState<string>('')
  const [selectedParcelFila, setSelectedParcelFila] = useState<string>('')
  const [selectedParcelId, setSelectedParcelId] = useState<string>('')
  const [selectedParcelState, setSelectedParcelState] = useState<ParcelState>(ParcelState.OCCUPIED)
  const [selectedRedeemParcelSection, setSelectedRedeemParcelSection] = useState<string>('')
  const [selectedRedeemParcelFila, setSelectedRedeemParcelFila] = useState<string>('')
  const [selectedRedeemParcelId, setSelectedRedeemParcelId] = useState<string>('')
  const [selectedStatus, setSelectedStatus] = useState('')
  const actionMutation = useAgreementAction()

  const isModifyParcel = selectedAction === AgreementAction.ModifyParcel
  const activeSection = isModifyParcel ? selectedParcelSection : selectedRedeemParcelSection
  const { data: availableParcelsData } = useParcels(
    activeSection ? { state: ParcelState.AVAILABLE, limit: 1000, section: activeSection } : undefined
  )
  const availableParcels = availableParcelsData?.data ?? []

  useEffect(() => {
    if (parcel) {
      setSelectedParcelSection(parcel.section)
      setSelectedParcelFila(parcel.fila.toString())
    }
  }, [parcel])

  const handleActionClick = (actionId: AgreementAction) => {
    setSelectedAction(prev => (prev === actionId ? null : actionId))
  }

  const handleConfirmSelected = () => {
    if (selectedActionData) {
      if (confirm('¿Confirma que desea realizar esta acción?')) {
        handleExecuteAction(selectedActionData.id)
      }
    }
  }

  const handleTogglePeople = (personId: string) => {
    setSelectedPeopleIds(prev =>
      prev.includes(personId)
        ? prev.filter(id => id !== personId)
        : [...prev, personId]
    )
  }

  const redeemNeedsParcel = selectedRedemptionType === RedemptionType.PARCEL || selectedRedemptionType === RedemptionType.FUNERAL_PARCEL

  const handleExecuteAction = (id: AgreementAction) => {
    const request: AgreementActionRequest = {
      contractId: agreement.id,
      action: id,
    }

    if (id === AgreementAction.ModifyParcel && selectedParcelId) {
      request.parcel = {
        id: parcel?.id ?? null,
        newParcelId: selectedParcelId,
        state: selectedParcelState,
      }
    }
    else if (id === AgreementAction.RedeemPeople && subscriptionId && people && people.length > 0 && selectedPeopleIds && selectedPeopleIds.length > 0) {
      request.redeem = {
        type: selectedRedemptionType,
        subscriptionId,
        contractId: agreement.id,
        ids: selectedPeopleIds,
        parcelId: redeemNeedsParcel ? selectedRedeemParcelId : undefined,
      }
    }
    else if (id === AgreementAction.ModifyStatus && selectedStatus) {
      request.status = selectedStatus
    }
    else return;
    // console.log(request)
    actionMutation.mutateAsync(request).then(() => {
      setSelectedAction(null)
      setSelectedPeopleIds([])
      onClose()
    })
  }

  const actions: ActionDefinition[] = []

  actions.push({
    id: AgreementAction.ModifyStatus,
    label: 'Cambiar estado',
    icon: <Power className="h-3.5 w-3.5" />,
    alertTitle: actionConsequences.modifyStatus.title,
    alertDescription: actionConsequences.modifyStatus.description,
    onConfirm: () => handleExecuteAction(AgreementAction.ModifyStatus),
    isPending: actionMutation.isPending,
  })

  if (serviceType === ServiceType.ABONOS) {
    actions.push({
      id: AgreementAction.RedeemPeople,
      label: 'Redimir un abonado',
      icon: <UserCheck className="h-3.5 w-3.5" />,
      alertTitle: actionConsequences.redeem.title,
      alertDescription: actionConsequences.redeem.description,
      onConfirm: () => handleExecuteAction(AgreementAction.RedeemPeople),
      isPending: actionMutation.isPending,
      disabled: !subscriptionId,
    })
  } else if (serviceType === ServiceType.PARCELAS) {
    actions.push({
      id: AgreementAction.ModifyParcel,
      label: 'Cambiar parcela',
      icon: <MapPin className="h-3.5 w-3.5" />,
      alertTitle: actionConsequences.changeParcel.title,
      alertDescription: actionConsequences.changeParcel.description,
      onConfirm: () => handleExecuteAction(AgreementAction.ModifyParcel),
      isPending: actionMutation.isPending,
      // disabled: !parcelId,
    })
  }

  const noActions = needsNoActions(serviceType)
  const selectedActionData = actions.find(a => a.id === selectedAction)

  const agreementStatusOptions = Object.values(AgreementStatus)
    .filter(s => s !== AgreementStatus.Expired && (serviceType === ServiceType.CREMACIONES ? s !== AgreementStatus.Active : s !== AgreementStatus.Pending))

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) { setSelectedAction(null); setSelectedRedemptionType(RedemptionType.CREMATION); setSelectedPeopleIds([]); setSelectedParcelSection(''); setSelectedParcelFila(''); setSelectedParcelId(''); setSelectedParcelState(ParcelState.OCCUPIED); setSelectedRedeemParcelSection(''); setSelectedRedeemParcelFila(''); setSelectedRedeemParcelId(''); setSelectedStatus(''); }
      if (!open) onClose();
    }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-warning">
            <AlertCircle className="h-5 w-5" />
            Acciones del contrato
          </DialogTitle>
          <DialogDescription>
            Seleccione la acción que desea realizar sobre este contrato.
          </DialogDescription>
        </DialogHeader>

        {noActions ? (
          <div className="my-4 flex justify-center">
            <Badge variant="outline" className="flex items-center gap-2 px-3 py-1.5 text-sm bg-warning/10 text-warning border-warning/30">
              <AlertCircle className="h-4 w-4" />
              Acciones no disponibles
            </Badge>
          </div>
        ) : (
          <div className="my-4 flex justify-between gap-4">
            {actions.map(action => (
              <IconButton
                key={action.id}
                variant="default"
                onClick={() => handleActionClick(action.id)}
                disabled={action.disabled || action.isPending}
                className={cn(
                  selectedAction === action.id && 'ring-2 ring-warning ring-offset-2 border-gray-600',
                )}
              >
                {action.icon}
                {action.label}
              </IconButton>
            ))}
          </div>
        )}

        {!noActions && selectedActionData && (
          <Alert variant="warning" className="mt-4">
            {selectedActionData.isPending ? <Power className="h-4 w-4 animate-spin" /> : <AlertTriangle className="h-4 w-4" />}
            <AlertTitle>{selectedActionData.alertTitle}</AlertTitle>
            <AlertDescription>{selectedActionData.alertDescription}</AlertDescription>
            {selectedAction === AgreementAction.RedeemPeople && people && (
              <div className="mt-3 space-y-2">
                <div className="space-y-1">
                  <p className="text-sm">Tipo de redención:</p>
                  <Select value={selectedRedemptionType} onValueChange={(v) => { setSelectedRedemptionType(v as RedemptionType); setSelectedRedeemParcelSection(''); setSelectedRedeemParcelFila(''); setSelectedRedeemParcelId(''); }}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Seleccione tipo de redención" />
                    </SelectTrigger>
                    <SelectContent>
                      {mapOptions(offering?.code || '').map((type) => (
                        <SelectItem key={type} value={type}>
                          {formatRedemptionType(type)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {redeemNeedsParcel && (
                  <div className="space-y-1">
                    <p className="text-sm ">Seleccione la parcela para la redención:</p>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="space-y-1">
                        <p className="text-sm">Sección:</p>
                        <Select value={selectedRedeemParcelSection} onValueChange={(v) => { setSelectedRedeemParcelSection(v); setSelectedRedeemParcelFila(''); setSelectedRedeemParcelId(''); }}>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Sección" />
                          </SelectTrigger>
                          <SelectContent>
                            {PARCEL_SECTIONS.map(section => (
                              <SelectItem key={section} value={section}>
                                {section}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm">Fila:</p>
                        <Select value={selectedRedeemParcelFila} onValueChange={(v) => { setSelectedRedeemParcelFila(v); setSelectedRedeemParcelId(''); }} disabled={!selectedRedeemParcelSection}>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Fila" />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from(new Set(availableParcels.filter(p => p.section === selectedRedeemParcelSection).map(p => p.fila))).sort((a, b) => a - b).map(fila => (
                              <SelectItem key={fila} value={String(fila)}>
                                {fila}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm">Parcela:</p>
                        <Select value={selectedRedeemParcelId} onValueChange={setSelectedRedeemParcelId} disabled={!selectedRedeemParcelFila}>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Parcela" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableParcels
                              .filter(p => p.section === selectedRedeemParcelSection && String(p.fila) === selectedRedeemParcelFila && p.id !== parcelId)
                              .sort((a, b) => a.parcela - b.parcela)
                              .map(p => (
                                <SelectItem key={p.id} value={p.id}>
                                  {p.parcela}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}
                <p className="text-sm ">Seleccione los abonados a redimir:</p>
                {people.map(person => (
                  <div
                    key={person.id}
                    className={`flex items-center gap-2 p-2 bg-gray-100/30 rounded-lg border cursor-pointer transition-colors ${selectedPeopleIds.includes(person.id || '')
                      ? 'bg-primary/10 border-primary'
                      : 'hover:bg-gray-200'
                      }`}
                    onClick={() => person.id && handleTogglePeople(person.id)}
                  >
                    <input
                      type="checkbox"
                      checked={person.id ? selectedPeopleIds.includes(person.id) : false}
                      onChange={() => person.id && handleTogglePeople(person.id)}
                      className="h-4 w-4 rounded"
                    />
                    <span className="text-sm capitalize">{person.name?.toLowerCase()}</span>
                    {person.status && (
                      <Badge className={`${getAssuredPersonStatusColor(person.status)} py-0 px-2 h-5 ml-auto`}>
                        {formatAssuredPersonStatus(person.status)}
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            )}
            {selectedAction === AgreementAction.ModifyParcel && (
              <div className="mt-3 space-y-2">
                <p className="text-sm ">Seleccione la nueva parcela:</p>
                <div className="grid grid-cols-3 gap-2">
                  <div className="space-y-1">
                    <p className="text-xs ">Sección:</p>
                    <Select value={selectedParcelSection} onValueChange={(v) => { setSelectedParcelSection(v); setSelectedParcelFila(''); setSelectedParcelId(''); }}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Sección" />
                      </SelectTrigger>
                      <SelectContent>
                        {PARCEL_SECTIONS.map(section => (
                          <SelectItem key={section} value={section}>
                            {section}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs ">Fila:</p>
                    <Select value={selectedParcelFila} onValueChange={(v) => { setSelectedParcelFila(v); setSelectedParcelId(''); }} disabled={!selectedParcelSection}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Fila" />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from(new Set(availableParcels.filter(p => p.section === selectedParcelSection).map(p => String(p.fila)))).sort().map(fila => (
                          <SelectItem key={fila} value={fila}>
                            {fila}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs ">Parcela:</p>
                    <Select value={selectedParcelId} onValueChange={setSelectedParcelId} disabled={!selectedParcelFila}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Parcela" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableParcels
                          .filter(p => p.section === selectedParcelSection && String(p.fila) === selectedParcelFila && p.id !== parcelId)
                          .sort((a, b) => a.parcela - b.parcela)
                          .map(p => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.parcela}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1">
                  <p className="text-xs ">Estado de la parcela:</p>
                  <Select value={selectedParcelState} onValueChange={(v) => setSelectedParcelState(v as ParcelState)}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[ParcelState.OCCUPIED, ParcelState.SOLD].map((s) => (
                        <SelectItem key={s} value={s}>
                          {formatParcelState(s)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            {selectedAction === AgreementAction.ModifyStatus && (
              <div className="mt-3 space-y-2">
                <p className="text-sm">Seleccione el nuevo estado:</p>
                <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {agreementStatusOptions.map(s => (
                      <SelectItem key={s} value={s}>
                        {formatAgreementStatus(s)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="mt-3 flex justify-between gap-2">
              <Button
                variant="default"
                size="sm"
                onClick={() => {
                  setSelectedAction(null)
                  setSelectedPeopleIds([])
                  setSelectedRedemptionType(RedemptionType.CREMATION)
                  setSelectedParcelSection('')
                  setSelectedParcelFila('')
                  setSelectedParcelId('')
                  setSelectedParcelState(ParcelState.OCCUPIED)
                  setSelectedRedeemParcelSection('')
                  setSelectedRedeemParcelFila('')
                  setSelectedRedeemParcelId('')
                  setSelectedStatus('')
                }}
              >
                <X className="h-3.5 w-3.5" />
                Cancelar
              </Button>
              <Button
                variant="info"
                size="sm"
                onClick={handleConfirmSelected}
                disabled={
                  (selectedAction === AgreementAction.RedeemPeople && ((!selectedPeopleIds || selectedPeopleIds.length === 0) || (redeemNeedsParcel && !selectedRedeemParcelId))) ||
                  (selectedAction === AgreementAction.ModifyParcel && (!selectedParcelId || (!parcel?.id && !selectedParcelState))) ||
                  (selectedAction === AgreementAction.ModifyStatus && !selectedStatus)
                }
              >
                <Check className="h-3.5 w-3.5" />
                Confirmar
              </Button>
            </div>
          </Alert>
        )}

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
